import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Pagina } from './Pagina.js';

/** One screen of a wizard: one question, what it means, and the way on. */
export interface Paso {
  id: string;
  /** The question, or what this step is about, in a few words. */
  titulo: string;
  /** What it means and why it is asked, in plain words. */
  explicacion?: ReactNode;
  contenido?: ReactNode;
  /**
   * The way forward. Omitted where the step's own buttons move on (a question with two answers).
   */
  seguir?: {
    etiqueta: string;
    icono?: NombreIcono;
    deshabilitado?: boolean;
    alTocar: () => void;
  };
  /** Nothing behind it can be changed any more: the till is open, or closed. */
  sinAtras?: boolean;
  /** A lesson's anchor (`data-guia`) on the step. */
  guia?: string;
}

/**
 * The frame of a wizard: which step of how many, the question, and «Atrás» and the way on at the
 * foot of the screen, in reach of a thumb however long the step is.
 *
 * The till's beginnings and endings are done by people who do them half asleep or at the end of
 * a long day, and some of them new (the owner, 2026-10-03): one thing at a time, said plainly,
 * and the reason with it, instead of a form that assumes everybody knows what a float, a corte or
 * general cash is.
 *
 * The cursor goes to the top of each new step, so a screen reader starts it from its question, and
 * the screen scrolls back up: a long count left the next step's question above the fold. So does a
 * count asked for again, the same step with a new question. Where it is says «2 de 8», not «Paso 2
 * de 8»: a lesson walking alongside says «Paso 7 de 12» over it.
 *
 * The way on is at the foot of a step that fills the screen, so on a phone the next step's comes up
 * where the last one's was, and for a moment after a step comes up it takes no tap (`DESCANSO_MS`):
 * the second tap of a hurried double tap meant for «Siguiente» landed on the next step's button,
 * and on the step before the last that is «Empezar el día» or «Terminar el día», done without the
 * check in between. Nor do its answers: one transfer's «Sí, aparece en el banco» tapped twice
 * would say the same of the next, unread.
 */
const DESCANSO_MS = 400;

export function Asistente({
  titulo,
  subtitulo,
  paso,
  numero,
  total,
  onAtras,
  volverA,
  pie,
}: {
  titulo: string;
  subtitulo?: string;
  paso: Paso;
  /** This step's place, from 1. */
  numero: number;
  total: number;
  onAtras?: () => void;
  volverA?: string;
  /** Below the step, outside it: what the screen showed before, the shifts gone by say. */
  pie?: ReactNode;
}) {
  const t = useT();
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    raiz.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [paso.id, paso.titulo]);
  // Before the screen is painted, so the first frame of a new step already takes no tap.
  const [descansando, setDescansando] = useState(false);
  useLayoutEffect(() => {
    setDescansando(true);
    const listo = setTimeout(() => setDescansando(false), DESCANSO_MS);
    return () => clearTimeout(listo);
  }, [paso.id, paso.titulo]);
  const atras = !paso.sinAtras && onAtras;

  return (
    // Room at the top for a lesson's strip, while one is walking somebody through (`suelta`).
    <div style={{ paddingTop: 'var(--tutorial-arriba, 0px)' }}>
    <Pagina titulo={titulo} subtitulo={subtitulo} {...(volverA ? { volverA } : {})}>
      <div className="mb-5">
        <p className="mb-2 text-xs font-medium text-slate-400">{t('asistente.paso', { n: numero, total })}</p>
        <div
          className="h-1.5 overflow-hidden rounded-full bg-slate-800"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={numero}
          aria-label={t('asistente.paso', { n: numero, total })}
        >
          <div className="h-full rounded-full bg-marca-500 transition-[width]" style={{ width: `${(numero / total) * 100}%` }} />
        </div>
      </div>

      <div
        ref={raiz}
        tabIndex={-1}
        className={`flex flex-col gap-4 outline-none ${descansando ? 'pointer-events-none' : ''}`}
        data-guia={paso.guia}
        data-paso={paso.id}
      >
        <h2 className="text-2xl font-bold text-balance">{paso.titulo}</h2>
        {paso.explicacion && <div className="text-base text-slate-300">{paso.explicacion}</div>}
        {paso.contenido}
      </div>

      {(atras || paso.seguir) && (
        <div className="sticky bottom-0 z-10 -mx-5 mt-6 flex gap-3 border-t border-slate-800 bg-slate-950/95 px-5 py-3 backdrop-blur">
          {atras && (
            <button type="button" className="btn-secundario px-5" onClick={atras}>
              <Ico icono="atras" />
              {t('asistente.atras')}
            </button>
          )}
          {paso.seguir && (
            <button
              type="button"
              className={`btn-primario min-w-0 flex-1 ${descansando ? 'pointer-events-none' : ''}`}
              disabled={paso.seguir.deshabilitado}
              onClick={paso.seguir.alTocar}
              data-asistente-seguir
            >
              <Ico icono={paso.seguir.icono ?? 'adelante'} />
              {paso.seguir.etiqueta}
            </button>
          )}
        </div>
      )}

      {pie}
    </Pagina>
    </div>
  );
}

/**
 * Which of `ids` is on screen: `actual`, or the first when `actual` is '' (the first step after the
 * till opened or closed, whichever that turns out to be). When the step on screen has gone, a list
 * that emptied while it was read, the step that took its place: `antes`, where it was, and not
 * back to the first, which after the corte is what the corte found.
 */
export function indicePaso(ids: readonly string[], actual: string, antes: number): number {
  const i = ids.indexOf(actual);
  if (i >= 0) return i;
  return actual === '' ? 0 : Math.max(0, Math.min(antes, ids.length - 1));
}

/**
 * A question with its answers as big buttons, one under the other: the step moves on with the tap.
 * The answer given is marked (`elegida`): one that opens something more to say stays on screen,
 * and «Atrás» comes back to a question already answered. None takes a tap while one is being
 * saved (`deshabilitado`).
 */
export function Respuestas({
  opciones,
  deshabilitado = false,
}: {
  opciones: readonly {
    etiqueta: string;
    detalle?: string;
    icono: NombreIcono;
    alTocar: () => void;
    elegida?: boolean;
    /** What tells it from the others when two say the same: two bills from one supplier. */
    clave?: string;
  }[];
  deshabilitado?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      {opciones.map((o) => (
        <button
          key={o.clave ?? o.etiqueta}
          type="button"
          className={`tarjeta-accion flex w-full items-center gap-4 p-4 text-left ${o.elegida ? 'ring-2 ring-marca-400' : ''}`}
          aria-pressed={o.elegida ?? false}
          disabled={deshabilitado}
          onClick={o.alTocar}
        >
          <Ico icono={o.icono} className="size-7 shrink-0 text-marca-300" />
          <span className="min-w-0 flex-1">
            <span className="block text-lg font-semibold">{o.etiqueta}</span>
            {o.detalle && <span className="mt-0.5 block text-sm text-slate-400">{o.detalle}</span>}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
        </button>
      ))}
    </div>
  );
}
